import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Info, Loader2, ShieldCheck, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/Cards";
import { useMe, useOrgUsers, orgUsersKey, meKey } from "@/hooks/useMe";
import { api, type OrgRole, type OrgUserSummary } from "@/lib/api";

/**
 * Settings → Team
 *
 * Admin-only management surface for org member roles. Members see a
 * read-only list. Admins can promote/demote everyone, with a server-side
 * guard that prevents removing the last admin.
 *
 * Role meanings:
 *   - admin   — manages org settings, members, brand, integrations.
 *   - finance — full access including pricing-sensitive sections.
 *   - member  — default; cannot see sections marked sensitive unless
 *                explicitly added to the section's allowlist.
 */
export default function SettingsTeam() {
  const me = useMe();
  const orgUsers = useOrgUsers();

  const isAdmin = me.data?.user.role === "admin";

  if (me.isLoading || orgUsers.isLoading) {
    return (
      <Layout>
        <PageHeader title="Team" subtitle="Loading…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  const users: OrgUserSummary[] = orgUsers.data?.users ?? [];

  return (
    <Layout>
      <PageHeader
        title="Team"
        subtitle={
          isAdmin
            ? "Promote, demote, and review who can see sensitive sections."
            : "Members of your organization and their roles."
        }
      />

      <div className="p-8 pb-24 space-y-4 max-w-3xl">
        <Card>
          <div className="flex items-start gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
            >
              <Info size={16} style={{ color: "#F97316" }} />
            </div>
            <div className="text-xs" style={{ color: "#9CA3AF" }}>
              <p className="text-white text-sm font-medium mb-1">Role overview</p>
              <p className="mb-1.5">
                <strong className="text-white">Admin</strong> — manages org settings, members,
                brand, and integrations. Sees every section, including pricing.
              </p>
              <p className="mb-1.5">
                <strong className="text-white">Finance</strong> — full access including
                pricing-sensitive sections. Cannot manage org or members.
              </p>
              <p>
                <strong className="text-white">Member</strong> — default. Cannot see sections marked
                sensitive unless added to the section's allowlist.
              </p>
            </div>
          </div>
        </Card>

        <div className="space-y-2">
          {users.map((u) => (
            <UserRow key={u.id} user={u} isMe={u.id === me.data?.user.id} canEdit={isAdmin} />
          ))}
        </div>
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function UserRow({
  user,
  isMe,
  canEdit,
}: {
  user: OrgUserSummary;
  isMe: boolean;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<OrgRole | null>(null);

  const mut = useMutation({
    mutationFn: (role: OrgRole) => api.updateOrgUser(user.id, { role }),
    onSuccess() {
      qc.invalidateQueries({ queryKey: orgUsersKey });
      if (isMe) {
        // If you demoted yourself, re-fetch /me so the UI's role gates
        // update right away (no full reload required).
        qc.invalidateQueries({ queryKey: meKey });
      }
      toast.success("Role updated.");
    },
    onError(err) {
      toast.error((err as Error).message);
    },
    onSettled() {
      setPending(null);
    },
  });

  const initials = (user.displayName ?? user.email).slice(0, 2).toUpperCase() || "??";

  return (
    <div
      className="rounded-xl border px-4 py-3 flex items-center gap-3"
      style={{
        backgroundColor: "#161B22",
        borderColor: "rgba(229,231,235,0.08)",
      }}
    >
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
        style={{ backgroundColor: "rgba(249,115,22,0.2)" }}
      >
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {user.displayName || user.email}
          {isMe && (
            <span className="ml-2 text-[10px]" style={{ color: "#6B7280" }}>
              (you)
            </span>
          )}
        </p>
        {user.displayName && (
          <p className="text-xs truncate" style={{ color: "#9CA3AF" }}>
            {user.email}
          </p>
        )}
      </div>

      {canEdit ? (
        <select
          value={pending ?? user.role}
          disabled={mut.isPending}
          onChange={(e) => {
            const next = e.target.value as OrgRole;
            setPending(next);
            mut.mutate(next);
          }}
          className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white outline-none disabled:opacity-50"
        >
          <option value="admin">Admin</option>
          <option value="finance">Finance</option>
          <option value="member">Member</option>
        </select>
      ) : (
        <RoleBadge role={user.role} />
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: OrgRole }) {
  const color = role === "admin" ? "#F97316" : role === "finance" ? "#A78BFA" : "#9CA3AF";
  const Icon = role === "member" ? UserIcon : ShieldCheck;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md"
      style={{
        color,
        backgroundColor: "rgba(255,255,255,0.04)",
      }}
    >
      <Icon size={11} />
      {role}
    </span>
  );
}
